import { CrmProvider, ECrmFieldEditAccess } from 'types/crm';
import { TDashboardDataType, TDashboardFieldType } from 'types/dashboard';

export type TRevIntelFieldType =
  | TDashboardFieldType
  | 'dealHealth'
  | 'aiMethodology'
  | 'dealRisk'
  | 'dealStage'
  | 'deals';

export type TEditableCellValue =
  | string
  | number
  | boolean
  | string[]
  | null
  | Date;

export const enum ERevIntelDashboard {
  deal = 'deal',
  company = 'accounts',
  forecastSubmission = 'forecastSubmission',
  contact = 'contacts',
  lead = 'leads'
}

export type TRevIntelEngagementType = 'meeting' | 'call' | 'email';

export interface IRevIntelOrderBy {
  ruleUid: string;
  ruleObjectType: string | null;
}

interface IRevIntelColumnFieldRef {
  uuid?: string | null;
  externalId?: string | null;
  fieldLabel?: string;
  dataType?: string;
  fieldType?: string;
  fieldClass?: string;
  optionsJson?: any;
}

export interface IRevIntelColumn {
  uuid: string;
  order: number;
  groupName: string;
  label: string;
  isCrmData: boolean;
  dataType: string;
  fieldType: string;
  isEditable: boolean;
  isReadOnly: boolean;
  editableBy?: ECrmFieldEditAccess;
  sortKey: string | null;
  options:
    | Array<{
        label: string;
        value: string;
        source?: string;
        hidden?: boolean;
        description?: string;
        displayOrder?: number;
      }>
    | Record<string, any>;
  objectType: string | null;
  avomaField: IRevIntelColumnFieldRef | null;
  avField?: IRevIntelColumnFieldRef | null;
  crmField?: IRevIntelColumnFieldRef | null;
  orderBy: IRevIntelOrderBy | null;
  filterKey?: string | null;
  referenceKey: string | null;
  isSummarized: boolean;
  /** Optional tooltip shown next to the column header (e.g. info icon). */
  headerTooltip?: string;
}

export interface IRevIntelCrmPropertyUpdatePayload {
  uuid: string;
  provider: string;
  crmObjUuid?: string;
  propertyUuid: string;
  value: TEditableCellValue;
  objectType: string;
  dashboardUuid: string;
}

export interface IRevIntelAvomaPropertyUpdatePayload {
  uuid: string;
  provider: string;
  propertyUuid: string;
  value: TEditableCellValue;
  crmObjUuid?: string;
  dashboardUuid: string;
}

export interface IRevIntelField {
  uuid: string;
  fieldLabel: string;
  dataType: string;
  fieldType: string;
  fieldClass: string;
  optionsJson: any;
}

export interface IRevIntelAccountDetail {
  uuid: string;
  name: string;
  crmObjUuid: string;
  provider: string;
}

export interface IRevIntelDeal {
  uuid: string;
  name: string;
  crmObjUuid: string;
  provider: string;
  account: IRevIntelAccountDetail;
  fields: IRevIntelField[];
  object_url: string;
  owner_has_license?: boolean;
  onQuickViewChange?: (params: { uuid: string; [key: string]: any }) => void;
}

export interface IRevIntelCustomField {
  [uuid: string]: {
    value: string | null;
  };
}

export type TRevIntelField = IRevIntelDeal & IRevIntelCustomField;
export interface IRevIntelDealData {
  deals: IRevIntelDeal[];
  totalCount: number;
  hasMore: boolean;
}

export interface IRevIntelRowHeaderProps {
  item: TRevIntelField;
  crm: CrmProvider;
  column: IRevIntelColumn;
  onQuickViewChange: (params: { uuid: string; [key: string]: any }) => void;
  isReservedView: boolean;
  provider: string;
  currentDashboardViewUuid: string;
  isQuickViewSelected?: boolean;
}

export interface IRevIntelCellRendererProps {
  item: IRevIntelDeal;
  column: IRevIntelColumn;
  currentDashboardViewUuid: string;
  isReservedView: boolean;
  dashboardKey: ERevIntelDashboard;
  ownerHasLicense?: boolean;
  dealNameColumn?: IRevIntelColumn;
}

export interface IRevIntelDealHealth {
  score?: number;
  status?: 'low' | 'medium' | 'high';
  message?: string;
  lastUpdated?: string;
  [key: string]: any;
}

export interface IRevIntelAccountIdData {
  key: string;
  name?: string;
  uuid?: string;
  object_url?: string;
}

export type TRevIntelObjectType =
  | 'opportunity'
  | 'account'
  | 'contact'
  | 'lead';
